import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plane, Hotel, ArrowLeft, ArrowRight, Check, AlertTriangle,
  Loader2, RefreshCw, Clock, CreditCard, Shield,
  XCircle, AlertCircle, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import {
  FlightBookingConfirmation,
  HotelBookingConfirmation
} from '../components/booking/BookingConfirmation';
import BookingIdentificationModal from '../components/booking/BookingIdentificationModal';
import { useAuth } from '../context/AuthContext';
import { FlightInsuranceOffer } from '../components/booking/FlightInsuranceOffer';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID;

// Load Razorpay script
const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

// Booking flow states - Complete state machine
const FLOW_STATES = {
  LOADING: 'loading',
  REVIEW: 'review',
  CONFIRMATION: 'confirmation',
  FARE_CHANGED: 'fare_changed', // New state for fare change handling
  INITIATING_PAYMENT: 'initiating_payment',
  PAYMENT_PENDING: 'payment_pending',
  PAYMENT_PROCESSING: 'payment_processing',
  PAYMENT_VERIFYING: 'payment_verifying',
  BOOKING_EXECUTING: 'booking_executing',
  BOOKING_VERIFYING: 'booking_verifying',
  SUCCESS: 'success',
  PAYMENT_FAILED: 'payment_failed',
  BOOKING_FAILED: 'booking_failed',
  ERROR: 'error'
};

// Error type constants matching backend classification
const ERROR_TYPES = {
  FARE_EXPIRED: 'FARE_EXPIRED',
  FARE_CHANGED: 'FARE_CHANGED',
  SEAT_UNAVAILABLE: 'SEAT_UNAVAILABLE',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  API_ERROR: 'API_ERROR',
  BOOKING_ERROR: 'BOOKING_ERROR'
};

// User-friendly error messages for each error type
const ERROR_MESSAGES = {
  [ERROR_TYPES.FARE_EXPIRED]: {
    title: 'Fare Expired',
    message: 'This fare is no longer available. Flight prices change frequently based on demand.',
    action: 'Please search again for current prices.'
  },
  [ERROR_TYPES.FARE_CHANGED]: {
    title: 'Fare Changed',
    message: 'The price for this flight has changed since your search.',
    action: 'Please review the new price below.'
  },
  [ERROR_TYPES.SEAT_UNAVAILABLE]: {
    title: 'Seats Unavailable',
    message: 'The selected seats are no longer available on this flight.',
    action: 'Please search again or try a different flight.'
  },
  [ERROR_TYPES.VALIDATION_ERROR]: {
    title: 'Validation Error',
    message: 'There was an issue with your booking request.',
    action: 'Please try again or contact support.'
  },
  [ERROR_TYPES.API_ERROR]: {
    title: 'Service Unavailable',
    message: 'We could not connect to the booking service.',
    action: 'Please try again in a few moments.'
  },
  [ERROR_TYPES.BOOKING_ERROR]: {
    title: 'Booking Error',
    message: 'There was an issue processing your booking.',
    action: 'Please try again or contact support.'
  }
};

const CompleteBookingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  // Core state
  const [flowState, setFlowState] = useState(FLOW_STATES.LOADING);
  const [bookingType, setBookingType] = useState(null);
  const [selectionData, setSelectionData] = useState(null);
  const [reviewData, setReviewData] = useState(null);
  const [pricingData, setPricingData] = useState(null);
  
  // Customer identification state
  const [showIdentificationModal, setShowIdentificationModal] = useState(false);
  const [customerData, setCustomerData] = useState(null);
  
  // Session state
  const [sessionId, setSessionId] = useState(null);
  const [razorpayOrderId, setRazorpayOrderId] = useState(null);
  
  // Result state
  const [paymentResult, setPaymentResult] = useState(null);
  const [bookingResult, setBookingResult] = useState(null);
  const [verificationResult, setVerificationResult] = useState(null);
  const [error, setError] = useState(null);
  
  // Error state for detailed error info
  const [errorInfo, setErrorInfo] = useState(null); // { type, code, message, currentFare, originalFare }
  
  // Seat selection state
  const [seatMapData, setSeatMapData] = useState(null);
  const [selectedSeats, setSelectedSeats] = useState({}); // { passengerId: seatCode }
  const [seatRequired, setSeatRequired] = useState(false);
  const [loadingSeatMap, setLoadingSeatMap] = useState(false);
  
  // Refs for preventing duplicate actions
  const processingRef = useRef(false);
  const paymentInitiatedRef = useRef(false);
  const pendingConfirmDataRef = useRef(null);
  const reviewInitiatedRef = useRef(false);

  // Load Razorpay on mount
  useEffect(() => {
    loadRazorpayScript();
  }, []);

  // Get auth token
  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  }, []);
  
  // Fetch seat map for flight booking
  const fetchSeatMap = useCallback(async (bookingId) => {
    setLoadingSeatMap(true);
    try {
      const response = await fetch(`${API_URL}/api/tripjack/flights/seat-map/${bookingId}`);
      const data = await response.json();
      
      if (data.success && data.hasSeatMap) {
        setSeatMapData(data);
        console.log('Seat map loaded:', data.segments?.length, 'segments');
      } else if (data.message) {
        console.log('Seat selection not required:', data.message);
        setSeatRequired(false);
      }
    } catch (err) {
      console.error('Failed to load seat map:', err);
    } finally {
      setLoadingSeatMap(false);
    }
  }, []);

  // Step 1: Perform TripJack review/validation with proper error handling
  const performReview = useCallback(async (type, selection) => {
    // Prevent duplicate review calls
    if (reviewInitiatedRef.current) {
      console.log('Review already in progress, skipping duplicate call');
      return;
    }
    reviewInitiatedRef.current = true;
    
    try {
      const headers = getAuthHeaders();

      if (type === 'flight') {
        const priceIdsToSend = selection.priceIds || [selection.priceId];
        
        const response = await fetch(`${API_URL}/api/safe-booking/flight/pre-validate`, {
          method: 'POST',
          headers,
          body: JSON.stringify(priceIdsToSend)
        });
        const data = await response.json();
        
        if (data.success) {
          // Check if fare has changed from original search price
          const validatedFare = data.validated_fare;
          const originalFare = selection.price;
          
          if (validatedFare && originalFare && Math.abs(validatedFare - originalFare) > 1) {
            // Fare has changed - show fare change UI
            console.log(`Fare changed: Original ₹${originalFare} -> Current ₹${validatedFare}`);
            setErrorInfo({
              type: ERROR_TYPES.FARE_CHANGED,
              originalFare: originalFare,
              currentFare: validatedFare,
              message: `The price has changed from ₹${originalFare.toLocaleString()} to ₹${validatedFare.toLocaleString()}`
            });
            setReviewData(data);
            setFlowState(FLOW_STATES.FARE_CHANGED);
          } else {
            // Price is same or very close - proceed normally
            setReviewData(data);
            
            // Check if seat selection is required/available for flights
            const conditions = data.conditions || {};
            const isSeatRequired = conditions.isSeatsRequired || conditions.iss || conditions.ifsm;
            setSeatRequired(isSeatRequired);
            
            // If flight and seat info available, fetch seat map
            if (data.booking_id && conditions.fsc?.issi) {
              fetchSeatMap(data.booking_id);
            }
            
            setFlowState(FLOW_STATES.CONFIRMATION);
          }
        } else {
          // Handle structured error from backend
          console.error('Pre-validate failed:', data);
          const errorType = data.error_type || ERROR_TYPES.API_ERROR;
          const errorCode = data.error_code || 'UNKNOWN';
          const errorMsg = data.error || 'Fare validation failed';
          const currentFare = data.current_fare;
          
          // Set detailed error info for UI
          setErrorInfo({
            type: errorType,
            code: errorCode,
            message: errorMsg,
            currentFare: currentFare,
            originalFare: selection.price
          });
          
          // Handle fare changed scenario specially
          if (errorType === ERROR_TYPES.FARE_CHANGED && currentFare) {
            setFlowState(FLOW_STATES.FARE_CHANGED);
          } else {
            setError(errorMsg);
            setFlowState(FLOW_STATES.ERROR);
          }
        }
      } else if (type === 'hotel') {
        const response = await fetch(
          `${API_URL}/api/safe-booking/hotel/pre-validate?correlation_id=${selection.correlationId}&hotel_id=${selection.hotelId}&option_id=${selection.optionId}&review_hash=${encodeURIComponent(selection.reviewHash)}`,
          { method: 'POST', headers }
        );
        const data = await response.json();
        
        if (data.success) {
          setReviewData(data);
          setFlowState(FLOW_STATES.CONFIRMATION);
        } else {
          const errorType = data.error_type || ERROR_TYPES.API_ERROR;
          const errorMsg = data.error || 'Hotel validation failed';
          
          setErrorInfo({
            type: errorType,
            code: data.error_code,
            message: errorMsg
          });
          setError(errorMsg);
          setFlowState(FLOW_STATES.ERROR);
        }
      }
    } catch (err) {
      console.error('Review error:', err);
      setErrorInfo({
        type: ERROR_TYPES.API_ERROR,
        message: err.message || 'An unexpected error occurred'
      });
      setError(err.message);
      setFlowState(FLOW_STATES.ERROR);
    }
  }, [getAuthHeaders]);

  // Initialize from location state
  useEffect(() => {
    const initBooking = async () => {
      const state = location.state;
      
      if (!state) {
        toast.error('No booking data found');
        navigate('/travel');
        return;
      }

      setBookingType(state.type);
      setSelectionData(state.selection);
      
      if (state.reviewData) {
        setReviewData(state.reviewData);
      }
      
      if (state.pricingData) {
        setPricingData(state.pricingData);
      }

      if (state.reviewData) {
        setFlowState(FLOW_STATES.CONFIRMATION);
      } else {
        setFlowState(FLOW_STATES.REVIEW);
        await performReview(state.type, state.selection);
      }
    };

    initBooking();
  }, [location.state, navigate, performReview]);

  // Handle customer identification
  const handleCustomerIdentified = (data) => {
    setCustomerData(data);
    setShowIdentificationModal(false);
    // Update selection data with customer info
    const updatedSelectionData = {
      ...selectionData,
      contactEmail: data.email || selectionData?.contactEmail,
      contactPhone: data.mobile || selectionData?.contactPhone,
      contactName: data.name || selectionData?.contactName,
      customerId: data.customerId
    };
    setSelectionData(updatedSelectionData);
    toast.success('Details confirmed! Proceeding to payment...');
    // Now proceed with payment using stored confirm data
    proceedToPayment(pendingConfirmDataRef.current, updatedSelectionData);
  };

  // Handle accepting new price after fare change
  const handleAcceptNewPrice = () => {
    if (!errorInfo?.currentFare || !reviewData) {
      toast.error('Unable to proceed. Please search again.');
      navigate('/travel');
      return;
    }
    
    // Update selection data with new price
    const updatedSelectionData = {
      ...selectionData,
      price: errorInfo.currentFare,
      originalPrice: errorInfo.originalFare,
      priceAccepted: true
    };
    setSelectionData(updatedSelectionData);
    
    // Clear error info and proceed to confirmation
    setErrorInfo(null);
    setError(null);
    toast.success('New price accepted. Proceeding to booking...');
    setFlowState(FLOW_STATES.CONFIRMATION);
  };

  // Handle rejecting new price - go back to search
  const handleRejectNewPrice = () => {
    toast.info('Returning to search for fresh prices.');
    navigate('/travel');
  };

  // Check if user needs identification before payment
  const initiateBookingWithPayment = async (confirmData) => {
    // Store confirm data for later use
    pendingConfirmDataRef.current = confirmData;
    
    // If user is already authenticated or we have customer data, proceed directly
    if (isAuthenticated || customerData) {
      proceedToPayment(confirmData);
      return;
    }
    
    // Show identification modal
    setShowIdentificationModal(true);
  };

  // Actual payment initiation after identification
  const proceedToPayment = async (confirmData, updatedSelection = null) => {
    const confirmDataToUse = confirmData || pendingConfirmDataRef.current;
    const selectionToUse = updatedSelection || selectionData;
    
    if (!confirmDataToUse) {
      toast.error('Missing booking confirmation data');
      return;
    }
    if (processingRef.current || paymentInitiatedRef.current) {
      toast.warning('Payment already in progress');
      return;
    }

    processingRef.current = true;
    paymentInitiatedRef.current = true;
    setFlowState(FLOW_STATES.INITIATING_PAYMENT);

    try {
      // Prepare request data - extract amount from multiple sources
      const amount = confirmDataToUse.revalidatedFare || confirmDataToUse.amount || 
                     reviewData?.validated_fare || reviewData?.data?.totalPriceInfo?.totalFareDetail?.fC?.TF ||
                     pricingData?.options?.[0]?.pricing?.totalPrice || 0;

      const bookingId = confirmDataToUse.bookingId || reviewData?.booking_id;
      
      // Validate required fields before sending
      if (!bookingId) {
        throw new Error('Booking ID is missing. Please go back and try again.');
      }
      if (!amount || amount <= 0) {
        throw new Error('Invalid booking amount. Please refresh and try again.');
      }

      console.log('[Payment Debug] Initiating booking:', {
        bookingType,
        bookingId,
        amount,
        confirmDataToUse,
        reviewData: reviewData?.data ? 'has data' : 'no data'
      });

      const requestBody = {
        booking_type: bookingType,
        tripjackBookingId: bookingId,
        amount: amount,
        contact: {
          email: selectionToUse?.contactEmail || customerData?.email || 'guest@example.com',
          phone: selectionToUse?.contactPhone || customerData?.mobile || '+919876543210'
        }
      };

      if (bookingType === 'flight') {
        requestBody.passengers = selectionToUse?.passengers || [{
          title: 'Mr',
          firstName: 'Test',
          lastName: 'User',
          passengerType: 'ADULT'
        }];
      } else {
        // Use guests from confirmData (includes PAN if provided) or fallback
        requestBody.guests = confirmDataToUse?.guests || selectionToUse?.guests || [{
          title: 'Mr',
          firstName: 'Test',
          lastName: 'User'
        }];
      }

      // Create booking session
      const response = await fetch(`${API_URL}/api/booking-flow/initiate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.detail || 'Failed to initiate booking');
      }

      setSessionId(data.session_id);
      setRazorpayOrderId(data.razorpay.order_id);
      setFlowState(FLOW_STATES.PAYMENT_PENDING);

      // Open Razorpay checkout
      await openRazorpayCheckout(data);

    } catch (err) {
      console.error('[Payment Debug] Initiate booking error:', err, {
        confirmDataToUse,
        reviewData: reviewData ? 'exists' : 'null',
        bookingType
      });
      setError(err.message);
      setFlowState(FLOW_STATES.ERROR);
      processingRef.current = false;
      paymentInitiatedRef.current = false;
    }
  };

  // Step 3: Open Razorpay checkout
  const openRazorpayCheckout = async (sessionData) => {
    const scriptLoaded = await loadRazorpayScript();
    if (!scriptLoaded) {
      throw new Error('Failed to load payment gateway');
    }

    const options = {
      key: RAZORPAY_KEY_ID || sessionData.razorpay.key_id,
      amount: sessionData.razorpay.amount_paise,
      currency: sessionData.razorpay.currency || 'INR',
      name: 'ASK AIRA',
      description: `${bookingType === 'flight' ? 'Flight' : 'Hotel'} Booking`,
      order_id: sessionData.razorpay.order_id,
      prefill: {
        name: selectionData?.contactName || '',
        email: selectionData?.contactEmail || '',
        contact: selectionData?.contactPhone || ''
      },
      theme: {
        color: '#6366f1'
      },
      handler: async (response) => {
        // Payment successful - verify and proceed
        await handlePaymentSuccess(sessionData.session_id, response);
      },
      modal: {
        ondismiss: () => {
          setFlowState(FLOW_STATES.PAYMENT_FAILED);
          setError('Payment was cancelled');
          processingRef.current = false;
        },
        escape: false,
        animation: true
      }
    };

    const razorpay = new window.Razorpay(options);

    razorpay.on('payment.failed', async (response) => {
      await handlePaymentFailure(sessionData.session_id, response.error);
    });

    razorpay.open();
    setFlowState(FLOW_STATES.PAYMENT_PROCESSING);
  };

  // Step 4: Handle payment success
  const handlePaymentSuccess = async (sessId, razorpayResponse) => {
    setFlowState(FLOW_STATES.PAYMENT_VERIFYING);
    
    try {
      // Verify payment on backend
      const verifyResponse = await fetch(`${API_URL}/api/booking-flow/payment-success`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          sessionId: sessId,
          razorpayOrderId: razorpayResponse.razorpay_order_id,
          razorpayPaymentId: razorpayResponse.razorpay_payment_id,
          razorpaySignature: razorpayResponse.razorpay_signature
        })
      });

      const verifyData = await verifyResponse.json();

      if (!verifyData.success) {
        throw new Error(verifyData.detail?.error || 'Payment verification failed');
      }

      setPaymentResult(verifyData);
      toast.success('Payment verified!');

      // Proceed to booking
      await executeBooking(sessId);

    } catch (err) {
      console.error('Payment verification error:', err);
      setError(`Payment verification failed: ${err.message}`);
      setFlowState(FLOW_STATES.PAYMENT_FAILED);
      processingRef.current = false;
    }
  };

  // Step 5: Handle payment failure
  const handlePaymentFailure = async (sessId, error) => {
    setFlowState(FLOW_STATES.PAYMENT_FAILED);
    
    try {
      await fetch(`${API_URL}/api/booking-flow/payment-failure`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          sessionId: sessId,
          errorCode: error.code || 'UNKNOWN',
          errorDescription: error.description || error.reason || 'Payment failed'
        })
      });
    } catch (err) {
      console.error('Payment failure report error:', err);
    }

    setError(error.description || 'Payment failed');
    processingRef.current = false;
    paymentInitiatedRef.current = false;
  };

  // Step 6: Execute TripJack booking
  const executeBooking = async (sessId, skipActual = false) => {
    setFlowState(FLOW_STATES.BOOKING_EXECUTING);

    try {
      const response = await fetch(`${API_URL}/api/booking-flow/execute-booking`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          sessionId: sessId,
          skipActualBooking: skipActual // Set to true for testing without real booking
        })
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.detail?.error || data.detail?.message || 'Booking failed');
      }

      setBookingResult(data);
      toast.success('Booking initiated!');

      // Verify booking
      await verifyBooking(sessId);

    } catch (err) {
      console.error('Booking execution error:', err);
      setError(`Booking failed: ${err.message}. Refund will be processed.`);
      setFlowState(FLOW_STATES.BOOKING_FAILED);
      processingRef.current = false;
    }
  };

  // Step 7: Verify booking status
  const verifyBooking = async (sessId, immediate = true) => {
    setFlowState(FLOW_STATES.BOOKING_VERIFYING);

    try {
      const response = await fetch(`${API_URL}/api/booking-flow/verify-booking`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          sessionId: sessId,
          immediate: immediate
        })
      });

      const data = await response.json();
      setVerificationResult(data);

      if (data.success) {
        setFlowState(FLOW_STATES.SUCCESS);
        toast.success('Booking confirmed!');
      } else if (data.check_after_seconds) {
        // Hotel needs delayed verification
        setFlowState(FLOW_STATES.SUCCESS);
        toast.info(`Booking initiated. Final confirmation in ${Math.round(data.check_after_seconds / 60)} minutes.`);
      } else {
        setFlowState(FLOW_STATES.SUCCESS);
        toast.warning('Booking initiated. Please check status in My Bookings.');
      }

    } catch (err) {
      console.error('Verification error:', err);
      // Still show success - booking was made
      setFlowState(FLOW_STATES.SUCCESS);
      toast.warning('Booking initiated. Verification pending.');
    } finally {
      processingRef.current = false;
    }
  };

  // Retry payment
  const retryPayment = () => {
    processingRef.current = false;
    paymentInitiatedRef.current = false;
    setError(null);
    setFlowState(FLOW_STATES.CONFIRMATION);
  };

  // Render based on flow state
  const renderContent = () => {
    switch (flowState) {
      case FLOW_STATES.LOADING:
      case FLOW_STATES.REVIEW:
        return (
          <div className="flex flex-col items-center justify-center min-h-[400px]">
            <Loader2 className="w-12 h-12 animate-spin text-indigo-500 mb-4" />
            <p className="text-gray-400">Validating your selection...</p>
          </div>
        );

      case FLOW_STATES.CONFIRMATION:
        if (bookingType === 'flight') {
          return (
            <>
            <FlightInsuranceOffer flight={selectionData} travellers={selectionData?.adults || 1} />
            <FlightBookingConfirmation
              flightData={selectionData}
              reviewData={reviewData}
              onConfirm={initiateBookingWithPayment}
              onCancel={() => navigate(-1)}
              onBack={() => navigate(-1)}
              seatMapData={seatMapData}
              selectedSeats={selectedSeats}
              onSeatSelect={(passengerId, seat) => setSelectedSeats(prev => ({...prev, [passengerId]: seat}))}
              seatRequired={seatRequired}
              loadingSeatMap={loadingSeatMap}
            />
            </>
          );
        } else {
          return (
            <HotelBookingConfirmation
              hotelData={selectionData}
              reviewData={reviewData}
              pricingData={pricingData}
              onConfirm={initiateBookingWithPayment}
              onCancel={() => navigate(-1)}
              onBack={() => navigate(-1)}
            />
          );
        }

      case FLOW_STATES.FARE_CHANGED:
        return (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[var(--bg-secondary)] rounded-2xl border border-amber-500/30 p-8"
          >
            <div className="text-center mb-6">
              <div className="w-20 h-20 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-10 h-10 text-amber-400" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Price Has Changed</h2>
              <p className="text-gray-400">
                The fare for this {bookingType} has been updated since your search.
              </p>
            </div>

            {/* Price Comparison */}
            <div className="bg-white/5 rounded-xl p-6 mb-6">
              <div className="flex items-center justify-between gap-8">
                {/* Original Price */}
                <div className="text-center flex-1">
                  <p className="text-gray-400 text-sm mb-1">Original Price</p>
                  <p className="text-white/60 text-2xl line-through">
                    ₹{errorInfo?.originalFare?.toLocaleString()}
                  </p>
                </div>
                
                {/* Arrow */}
                <div className="flex-shrink-0">
                  <ArrowRight className="w-8 h-8 text-amber-400" />
                </div>
                
                {/* New Price */}
                <div className="text-center flex-1">
                  <p className="text-gray-400 text-sm mb-1">Current Price</p>
                  <p className={`text-3xl font-bold ${
                    errorInfo?.currentFare > errorInfo?.originalFare 
                      ? 'text-red-400' 
                      : 'text-green-400'
                  }`}>
                    ₹{errorInfo?.currentFare?.toLocaleString()}
                  </p>
                  {errorInfo?.currentFare && errorInfo?.originalFare && (
                    <p className={`text-sm mt-1 ${
                      errorInfo?.currentFare > errorInfo?.originalFare 
                        ? 'text-red-400/70' 
                        : 'text-green-400/70'
                    }`}>
                      {errorInfo?.currentFare > errorInfo?.originalFare 
                        ? `+₹${(errorInfo.currentFare - errorInfo.originalFare).toLocaleString()} increase`
                        : `-₹${(errorInfo.originalFare - errorInfo.currentFare).toLocaleString()} decrease`
                      }
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Flight/Hotel Summary */}
            {selectionData && (
              <div className="bg-white/5 rounded-xl p-4 mb-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-indigo-500/20 rounded-xl flex items-center justify-center">
                    {bookingType === 'flight' ? (
                      <Plane className="w-6 h-6 text-indigo-400" />
                    ) : (
                      <Hotel className="w-6 h-6 text-indigo-400" />
                    )}
                  </div>
                  <div>
                    <p className="text-white font-medium">
                      {bookingType === 'flight' 
                        ? `${selectionData.origin} → ${selectionData.destination}`
                        : selectionData.name
                      }
                    </p>
                    <p className="text-gray-400 text-sm">
                      {bookingType === 'flight' 
                        ? `${selectionData.airline} • ${selectionData.flightNumber}`
                        : selectionData.city
                      }
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Info Notice */}
            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-6">
              <p className="text-blue-200 text-sm">
                <strong>Why did the price change?</strong><br/>
                Flight and hotel prices are dynamic and can change based on demand, 
                availability, and time. We always show you the most current price before booking.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4">
              <button
                onClick={handleRejectNewPrice}
                className="flex-1 px-6 py-3 rounded-xl border border-white/20 text-white hover:bg-white/5 transition-colors"
              >
                Search Again
              </button>
              <button
                onClick={handleAcceptNewPrice}
                className="flex-1 px-6 py-3 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-medium hover:shadow-lg hover:shadow-indigo-500/25 transition-all"
              >
                Accept New Price
              </button>
            </div>
          </motion.div>
        );

      case FLOW_STATES.INITIATING_PAYMENT:
        return (
          <div className="flex flex-col items-center justify-center min-h-[400px]">
            <CreditCard className="w-12 h-12 text-indigo-500 mb-4 animate-pulse" />
            <p className="text-white text-lg">Creating payment order...</p>
            <p className="text-gray-400 text-sm mt-2">Please wait</p>
          </div>
        );

      case FLOW_STATES.PAYMENT_PENDING:
      case FLOW_STATES.PAYMENT_PROCESSING:
        return (
          <div className="flex flex-col items-center justify-center min-h-[400px]">
            <div className="relative">
              <Loader2 className="w-16 h-16 animate-spin text-indigo-500" />
              <CreditCard className="w-6 h-6 text-white absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
            </div>
            <p className="text-white text-lg mt-6">Complete payment in Razorpay</p>
            <p className="text-gray-400 text-sm mt-2">Do not close this window</p>
            
            {/* Test mode info */}
            {RAZORPAY_KEY_ID?.includes('test') && (
              <div className="mt-6 bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 max-w-sm">
                <p className="text-amber-200 text-sm font-medium">🧪 Test Mode</p>
                <p className="text-amber-200/70 text-xs mt-1">
                  Card: 4111 1111 1111 1111<br/>
                  Expiry: Any future date<br/>
                  CVV: Any 3 digits | OTP: 1234
                </p>
              </div>
            )}
          </div>
        );

      case FLOW_STATES.PAYMENT_VERIFYING:
        return (
          <div className="flex flex-col items-center justify-center min-h-[400px]">
            <Shield className="w-12 h-12 text-blue-500 mb-4 animate-pulse" />
            <p className="text-white text-lg">Verifying payment...</p>
            <p className="text-gray-400 text-sm mt-2">Checking payment signature</p>
          </div>
        );

      case FLOW_STATES.BOOKING_EXECUTING:
        return (
          <div className="flex flex-col items-center justify-center min-h-[400px]">
            <div className="relative">
              <Loader2 className="w-16 h-16 animate-spin text-green-500" />
              {bookingType === 'flight' ? (
                <Plane className="w-6 h-6 text-white absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
              ) : (
                <Hotel className="w-6 h-6 text-white absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
              )}
            </div>
            <p className="text-white text-lg mt-6">Confirming your booking...</p>
            <p className="text-gray-400 text-sm mt-2">Processing with travel provider</p>
          </div>
        );

      case FLOW_STATES.BOOKING_VERIFYING:
        return (
          <div className="flex flex-col items-center justify-center min-h-[400px]">
            <RefreshCw className="w-12 h-12 animate-spin text-blue-500 mb-4" />
            <p className="text-white text-lg">Verifying booking status...</p>
            <p className="text-gray-400 text-sm mt-2">Almost done</p>
          </div>
        );

      case FLOW_STATES.SUCCESS:
        return (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[var(--bg-secondary)] rounded-2xl border border-white/10 p-8 text-center"
          >
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-10 h-10 text-green-400" />
            </div>
            
            <h2 className="text-2xl font-bold text-white mb-2">Booking Confirmed!</h2>
            <p className="text-gray-400 mb-6">
              Your {bookingType} has been successfully booked.
            </p>

            {/* Payment Info */}
            {paymentResult && (
              <div className="bg-white/5 rounded-xl p-4 mb-4 text-left">
                <p className="text-gray-400 text-sm">Payment ID</p>
                <p className="text-white font-mono">{paymentResult.payment_id}</p>
              </div>
            )}

            {/* Booking Info */}
            {bookingResult && (
              <div className="bg-white/5 rounded-xl p-4 mb-4 text-left">
                <p className="text-gray-400 text-sm">Booking Reference</p>
                <p className="text-white font-mono text-lg">
                  {bookingResult.tripjack_booking_ref || sessionId}
                </p>
              </div>
            )}

            {/* Verification Status */}
            {verificationResult && (
              <div className={`rounded-xl p-4 mb-6 ${
                verificationResult.success 
                  ? 'bg-green-500/10 border border-green-500/30' 
                  : 'bg-amber-500/10 border border-amber-500/30'
              }`}>
                <div className="flex items-center gap-2 justify-center">
                  {verificationResult.success ? (
                    <Check className="w-5 h-5 text-green-400" />
                  ) : (
                    <Clock className="w-5 h-5 text-amber-400" />
                  )}
                  <span className={verificationResult.success ? 'text-green-400' : 'text-amber-400'}>
                    {verificationResult.success ? 'Verified' : 'Verification Pending'}
                  </span>
                </div>
                {verificationResult.check_after_seconds && (
                  <p className="text-amber-200/70 text-sm mt-2">
                    Final confirmation in ~{Math.round(verificationResult.check_after_seconds / 60)} minutes
                  </p>
                )}
              </div>
            )}

            <div className="flex gap-4 justify-center">
              <button
                onClick={() => navigate('/travel')}
                className="px-6 py-3 rounded-xl border border-white/20 text-white hover:bg-white/5"
              >
                Back to Travel
              </button>
              <button
                onClick={() => navigate('/my-bookings')}
                className="px-6 py-3 rounded-xl bg-indigo-600 text-white hover:bg-indigo-500"
              >
                View My Bookings
              </button>
            </div>
          </motion.div>
        );

      case FLOW_STATES.PAYMENT_FAILED:
        return (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[var(--bg-secondary)] rounded-2xl border border-white/10 p-8 text-center"
          >
            <div className="w-20 h-20 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-10 h-10 text-amber-400" />
            </div>
            
            <h2 className="text-2xl font-bold text-white mb-2">Payment Failed</h2>
            <p className="text-gray-400 mb-6">{error || 'Payment could not be completed.'}</p>

            <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-6">
              <p className="text-blue-200 text-sm">
                No charges have been made. You can try again.
              </p>
            </div>

            <div className="flex gap-4 justify-center">
              <button
                onClick={() => navigate(-1)}
                className="px-6 py-3 rounded-xl border border-white/20 text-white hover:bg-white/5"
              >
                Go Back
              </button>
              <button
                onClick={retryPayment}
                className="px-6 py-3 rounded-xl bg-indigo-600 text-white hover:bg-indigo-500"
              >
                Try Again
              </button>
            </div>
          </motion.div>
        );

      case FLOW_STATES.BOOKING_FAILED:
        return (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[var(--bg-secondary)] rounded-2xl border border-white/10 p-8 text-center"
          >
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-10 h-10 text-red-400" />
            </div>
            
            <h2 className="text-2xl font-bold text-white mb-2">Booking Failed</h2>
            <p className="text-gray-400 mb-4">{error}</p>

            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 mb-6">
              <div className="flex items-center gap-2 justify-center text-green-400">
                <RefreshCw className="w-5 h-5" />
                <span className="font-medium">Refund Initiated</span>
              </div>
              <p className="text-green-200/70 text-sm mt-2">
                Your payment will be refunded within 5-7 business days.
              </p>
            </div>

            <div className="flex gap-4 justify-center">
              <button
                onClick={() => navigate('/travel')}
                className="px-6 py-3 rounded-xl border border-white/20 text-white hover:bg-white/5"
              >
                Back to Travel
              </button>
              <button
                onClick={() => navigate('/support')}
                className="px-6 py-3 rounded-xl bg-indigo-600 text-white hover:bg-indigo-500"
              >
                Contact Support
              </button>
            </div>
          </motion.div>
        );

      case FLOW_STATES.ERROR:
      default:
        // Get the appropriate error info based on error type
        const errorTypeInfo = errorInfo?.type ? ERROR_MESSAGES[errorInfo.type] : null;
        const displayTitle = errorTypeInfo?.title || 'Something went wrong';
        const displayMessage = errorInfo?.message || error || 'An unexpected error occurred.';
        const displayAction = errorTypeInfo?.action || 'Please try again or search for a new booking.';
        
        return (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[var(--bg-secondary)] rounded-2xl border border-white/10 p-8 text-center"
          >
            <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${
              errorInfo?.type === ERROR_TYPES.FARE_EXPIRED ? 'bg-amber-500/20' :
              errorInfo?.type === ERROR_TYPES.SEAT_UNAVAILABLE ? 'bg-orange-500/20' :
              'bg-red-500/20'
            }`}>
              {errorInfo?.type === ERROR_TYPES.FARE_EXPIRED ? (
                <Clock className="w-10 h-10 text-amber-400" />
              ) : errorInfo?.type === ERROR_TYPES.SEAT_UNAVAILABLE ? (
                <AlertTriangle className="w-10 h-10 text-orange-400" />
              ) : (
                <AlertCircle className="w-10 h-10 text-red-400" />
              )}
            </div>
            
            <h2 className="text-2xl font-bold text-white mb-2">{displayTitle}</h2>
            <p className="text-gray-400 mb-2">{displayMessage}</p>
            <p className="text-gray-500 text-sm mb-6">{displayAction}</p>
            
            {/* Error code for debugging/support */}
            {errorInfo?.code && errorInfo.code !== 'UNKNOWN' && (
              <div className="bg-white/5 rounded-lg px-4 py-2 mb-6 inline-block">
                <p className="text-gray-500 text-xs">
                  Error Code: <span className="font-mono text-gray-400">{errorInfo.code}</span>
                </p>
              </div>
            )}

            <div className="flex gap-4 justify-center">
              <button
                onClick={() => {
                  setError(null);
                  setErrorInfo(null);
                  setFlowState(FLOW_STATES.REVIEW);
                  processingRef.current = false;
                  paymentInitiatedRef.current = false;
                  // Re-trigger review
                  if (selectionData && bookingType) {
                    performReview(bookingType, selectionData);
                  }
                }}
                className="px-6 py-3 rounded-xl border border-white/20 text-white hover:bg-white/5"
              >
                Try Again
              </button>
              <button
                onClick={() => navigate('/travel')}
                className="px-6 py-3 rounded-xl bg-indigo-600 text-white hover:bg-indigo-500"
              >
                Search Again
              </button>
            </div>
          </motion.div>
        );
    }
  };

  // Progress steps
  const getProgressSteps = () => {
    const steps = [
      { id: 'review', label: 'Review', done: ![FLOW_STATES.LOADING, FLOW_STATES.REVIEW].includes(flowState) },
      { id: 'confirm', label: 'Confirm', done: ![FLOW_STATES.LOADING, FLOW_STATES.REVIEW, FLOW_STATES.CONFIRMATION].includes(flowState) },
      { id: 'payment', label: 'Payment', done: [FLOW_STATES.BOOKING_EXECUTING, FLOW_STATES.BOOKING_VERIFYING, FLOW_STATES.SUCCESS].includes(flowState) },
      { id: 'booking', label: 'Booking', done: [FLOW_STATES.BOOKING_VERIFYING, FLOW_STATES.SUCCESS].includes(flowState) },
      { id: 'done', label: 'Done', done: flowState === FLOW_STATES.SUCCESS }
    ];
    return steps;
  };

  const isErrorState = [FLOW_STATES.ERROR, FLOW_STATES.PAYMENT_FAILED, FLOW_STATES.BOOKING_FAILED, FLOW_STATES.FARE_CHANGED].includes(flowState);

  return (
    <div className="min-h-screen bg-[var(--bg-primary)]">
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Header */}
        {flowState !== FLOW_STATES.SUCCESS && !isErrorState && (
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
            disabled={processingRef.current}
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>
        )}

        {/* Progress Steps */}
        {flowState !== FLOW_STATES.SUCCESS && !isErrorState && (
          <div className="flex items-center justify-center gap-1 mb-8">
            {getProgressSteps().map((step, idx) => (
              <React.Fragment key={step.id}>
                <div className={`flex items-center gap-1 ${step.done ? 'text-indigo-400' : 'text-gray-600'}`}>
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                    step.done ? 'bg-indigo-500 text-white' : 'bg-white/10 text-gray-400'
                  }`}>
                    {step.done ? <Check className="w-3 h-3" /> : idx + 1}
                  </div>
                  <span className="text-xs hidden sm:inline">{step.label}</span>
                </div>
                {idx < getProgressSteps().length - 1 && (
                  <div className={`w-8 h-0.5 ${step.done ? 'bg-indigo-500' : 'bg-white/10'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Main Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={flowState}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            {renderContent()}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Booking Identification Modal - Shows before payment for guest users */}
      <BookingIdentificationModal
        isOpen={showIdentificationModal}
        onClose={() => setShowIdentificationModal(false)}
        onIdentified={handleCustomerIdentified}
        bookingType={bookingType}
        bookingDetails={selectionData}
      />
    </div>
  );
};

export default CompleteBookingPage;
